/**
 * Internal Playground — Hiring Capacity Timeline API service.
 *
 * Calls the People Space proxy's `/api/internal-playground/hiring-timeline` endpoint, which
 * authorizes `space.people.internal_playground.view` server-side (always enforced) — see
 * `proxy-server/routes/internal-playground.js` and `lib/internalPlaygroundTimeline.js` for the
 * full shape and how the `hiring_plan` (Airtable) and `dim_employee` (BigQuery) sources combine.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import type { RoleCompensation } from '../types/compensation';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

/** 'planned' is an open/backlog/on-hold role from `hiring_plan` — or an APPROVED proposal, which
 * is deliberately indistinguishable from one (see `demandRolesFromProposals` in
 * `internalPlaygroundTimeline.js`). 'proposed' is a role someone has asked for and nobody has
 * committed to yet: rendered purple, counted in `proposedFteByMonth` and never in `fteByMonth`. */
export type OrgLeafKind = 'actual' | 'planned' | 'proposed';

/** 'intern_ws' groups Interns and Working Students (`track_and_level === 'IC0'` for a real
 * employee, or the Role text for an open/planned role — see `employmentTypeOf` in
 * `internalPlaygroundTimeline.js`, the single place this is decided); everything else is
 * 'permanent'. Drives the Employment Type filter — see `timelineFilters.ts`. */
export type EmploymentType = 'intern_ws' | 'permanent';

/** A real employee or an open/planned role, grouped under a Role sort-header. `hireDate`/
 * `terminationDate` are only ever set for `kind === 'actual'` (Personio has no such dates for an
 * open/planned role) — 'YYYY-MM-DD', straight off `dim_employee`. */
export interface OrgLeaf {
  id: string;
  label: string;
  kind: OrgLeafKind;
  role: string;
  employmentType: EmploymentType;
  status?: string;
  undated?: boolean;
  /** Only ever set for `kind === 'proposed'` — the free text the proposer wrote. Display only:
   * nothing about placement or FTE reads it. */
  proposalRational?: string;
  fteByMonth: Record<string, number>;
  hireDate?: string | null;
  terminationDate?: string | null;
  /** Set only for a real employee with a termination date — see `BackfillState`. */
  backfill?: BackfillState;
  /** Planned and proposed roles only: which Airtable table the row lives in, and its record id —
   * what "Edit role" opens. Separate from `kind`, because an APPROVED proposal renders as
   * `planned` but still lives in the proposals table. Absent on an older proxy revision. */
  source?: 'proposal' | 'hiring_plan';
  recordId?: string;
  /** Planned and proposed roles only: the Personio ID of the hiring manager the tree places the
   * role under, null when unresolved. Read to decide whether a proposer is offered "Edit". */
  managerEmployeeId?: string | null;
  /** Hiring-plan rows only: a change over 10% is awaiting approval, and the row takes no other
   * edit until it is decided or withdrawn. */
  pendingChangeRequestId?: string;
}

/**
 * What the proposal form is pre-filled from when someone chooses to backfill a leaver. Raw Personio
 * values — the form keeps only the ones its Airtable vocabularies actually offer (`backfillForm`).
 */
export interface BackfillDraft {
  position: string | null;
  role: string | null;
  /** `dim_employee.team`, which uses the same "Growth - Sales" shape as the Airtable Unit. */
  team: string | null;
  department: string | null;
  supervisorId: string | null;
  trackAndLevel: string | null;
  /** One level below `trackAndLevel` — see `oneLevelBelow` on the proxy. */
  suggestedTrackAndLevel: string | null;
  costCategory: string;
}

/**
 * A leaver's backfill status, derived by the proxy (`internalPlaygroundBackfills.js`) on every
 * read. Optional wherever it appears: absent for anyone without a termination date, and for
 * everyone when the backfill sources could not be read (`dataQuality.backfillsUnavailable`).
 */
export type BackfillState =
  | { state: 'open'; draft: BackfillDraft }
  | { state: 'backfilled'; via: 'hiring_plan' | 'proposal'; title: string | null }
  | {
      state: 'not_backfilled';
      reason: 'decided' | 'proposal_declined' | 'left';
      decidedBy?: string | null;
      decidedAt?: string | null;
    };

export interface OrgRoleGroup {
  role: string;
  entries: OrgLeaf[];
}

/**
 * One node in the recursive org-chart cascade — the overall root ("FINN"), a department (a direct
 * child of the root, e.g. "Tech | Andreas Stryz"), or any person deeper in a reporting chain.
 * `groupLabel` is set only where a label is worth showing next to the person's name: the overall
 * root, a department node, or a node where the raw `team` changes from its parent (e.g.
 * "Sales | Julian Weller") — otherwise null. `subManagers` are direct reports who themselves have
 * reports (their own expandable row, never grouped by Role); `roleGroups` are direct reports with
 * no reports of their own, plus any open/planned roles attached here, grouped by Role.
 *
 * `hireDate`/`terminationDate` are this node's OWN Personio dates ('YYYY-MM-DD') — never a rollup
 * over `subManagers`/`roleGroups` — so the client can label a known leaver/incoming hire next to
 * this specific person's name.
 */
export interface OrgNode {
  id: string;
  personName: string;
  groupLabel: string | null;
  /** False only for the synthetic "Unassigned" bucket (`internalPlaygroundTimeline.js`'s
   * `unassignedNode`) and the synthetic CEO Office — every other node is a real employee, with an
   * employee id a backfill or a hiring manager can point at. */
  isPerson: boolean;
  /** Null for the "Unassigned" bucket, which is never a filterable "person" — see
   * `timelineFilters.ts`'s `filterOrgTree`: it is only ever kept on screen because a surviving child
   * role still needs it. The CEO Office carries the CEO's, because its own FTE is his. */
  role: string | null;
  /** Null for the "Unassigned" bucket; the CEO's on the CEO Office. */
  employmentType: EmploymentType | null;
  /** COMMITTED FTE only — real employees plus planned/approved roles. Proposed roles are
   * deliberately NOT in here (see `proposedFteByMonth`), so this figure, and the greyed-out
   * unfiltered comparison built from it, mean exactly what they meant before proposals existed. */
  fteByMonth: Record<string, number>;
  /** Proposed-role FTE rolled up over this subtree, rendered as the purple `+N` beside
   * `fteByMonth`. `{}` when nothing in this subtree is proposed. There is no `own` counterpart: a
   * node is always a real employee and a proposal is always a leaf, so a node's own FTE can never
   * be proposed.
   *
   * OPTIONAL because at runtime it genuinely can be absent: the frontend and the proxy deploy as
   * independent parallel jobs, so a payload from an older proxy revision has no such field.
   * Every reader must cope — see `filterOrgTree` and `FteCell`. */
  proposedFteByMonth?: Record<string, number>;
  /** This node's OWN FTE contribution only, excluding every `subManagers`/`roleGroups` rollup
   * already folded into `fteByMonth` above — sent only so a client-side filter can recompute the
   * rollup after pruning, without a second network round trip (see `timelineFilters.ts`'s
   * `filterOrgTree`). Always `{}` for the synthetic "Unassigned" bucket. */
  ownFteByMonth: Record<string, number>;
  subManagers: OrgNode[];
  roleGroups: OrgRoleGroup[];
  hireDate?: string | null;
  terminationDate?: string | null;
  /** Only on the CEO Office: the CEO's own dates, read by the filter's status alone, so his FTE on
   * that row is "leaving" when he is — without the date note `hireDate`/`terminationDate` would
   * print on the row. */
  filterDates?: { hireDate?: string | null; terminationDate?: string | null };
  /** Set only for a real employee with a termination date — see `BackfillState`. */
  backfill?: BackfillState;
}

export interface UnmatchedHire {
  id: string;
  positionId: string;
  title: string;
  role: string;
  department: string;
  actualStartDate: string | null;
  createdOn: string | null;
}

export interface UndatedDemandRole {
  id: string;
  positionId: string;
  title: string;
  department: string;
  status: string;
  /** True when the role came from the proposals table with `Status = 'Proposed'`. */
  proposed: boolean;
}

export interface OrgPlanningViewer {
  scope: 'full' | 'line';
  lineEmployeeIds: string[] | null;
}

export interface HiringCapacityTimeline {
  months: string[];
  root: OrgNode;
  dataQuality: {
    unmatchedHires: UnmatchedHire[];
    undatedDemandRoles: UndatedDemandRole[];
    /** A named org-chart override (the CEO/Operations-split exceptions) failed to match a real
     * employee — see `proxy-server/lib/internalPlaygroundTimeline.js`'s `buildOrgTree`. Each entry
     * is a ready-to-read sentence, not a code to look up. */
    orgOverrideWarnings: string[];
    /** Set when the proposals table could not be read at all — the timeline still renders, showing
     * committed roles only. Null on the happy path. A silent empty result would be
     * indistinguishable from "nobody has proposed anything". */
    proposalsUnavailable: string | null;
    /** Set when backfill status could not be read — no backfill state is attached anywhere then.
     * Optional: an older proxy revision does not send it. */
    backfillsUnavailable?: string | null;
    /** Set when long-term leave could not be read from the restricted DWH — FTE then still counts
     * people on parental leave, maternity leave or a sabbatical, i.e. it is too HIGH. Optional: an
     * older proxy revision does not send it. */
    leaveUnavailable?: string | null;
  };
  generatedAt: string;
  /**
   * What the page may OFFER this viewer. `full` is the existing audience; `line` is a proposer
   * (`org-planning-proposers`), who may only name — and edit roles under — the Personio IDs listed:
   * themselves and everyone reporting into them. Cosmetic; the proxy enforces the same thing.
   * Optional: an older proxy revision does not send it, and its absence means `full`.
   */
  viewer?: OrgPlanningViewer;
}

/** Thrown when the proxy denies access (403) so the UI can show a clear message. */
export class InternalPlaygroundAccessError extends Error {}

export async function fetchHiringCapacityTimeline(): Promise<HiringCapacityTimeline> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/internal-playground/hiring-timeline`, {
    suppressErrorStatuses: [403],
  });
  if (res.status === 403) {
    throw new InternalPlaygroundAccessError('You do not have access to the Internal Playground.');
  }
  if (!res.ok) {
    throw new Error(`Failed to fetch the hiring capacity timeline (${res.status})`);
  }
  return res.json();
}

/** The proposal form's dropdown vocabularies, read live from Airtable's schema by the proxy — so
 * a Role added in Airtable reaches the form with no deploy. Keys match `SELECT_FIELDS` in
 * `proxy-server/lib/internalPlaygroundProposals.js`. */
export interface ProposalFieldOptions {
  role: string[];
  peopleTeam: string[];
  unit: string[];
  trackAndLevel: string[];
  reasonForHiring: string[];
  costCategory: string[];
  /** The four ratings for the SUGGESTED priority. Optional: an older proxy does not send it. */
  priorityRating?: string[];
}

/** What the form sends. There is deliberately no `status` field: the proxy sets `Proposed`
 * itself, because a client able to name its own status could file a role as `Approved`, which the
 * timeline then counts as committed capacity. `hiringManagerEmployeeId` is the Personio ID the
 * org tree is already keyed on — the proxy resolves it to the Airtable record id the link field
 * stores, so the client never has to know that table exists. */
export interface ProposedRoleInput {
  title: string;
  role: string;
  peopleTeam: string;
  hiringManagerEmployeeId: string;
  plannedStartDate: string;
  proposalRational: string;
  unit?: string;
  trackAndLevel?: string;
  reasonForHiring?: string;
  costCategory?: string;
  /** The proposer's SUGGESTED priority (AAA/A/B/C). The approved rating is the CEO's, set on Role
   * Approvals — never from this form. */
  priorityRating?: string;
  /** The leaver this role replaces (Personio ID). Set only by the Manage backfill flow; the proxy
   * links it in `Backfill for` and files the role as a Backfill. */
  backfillForEmployeeId?: string;
}

/** Thrown when the proxy's Airtable token lacks `schema.bases:read` (501). Distinct from a
 * transport failure because the fix is a token scope, not a retry. */
export class ProposalOptionsUnavailableError extends Error {}

/** A 400 from the proxy, carrying which field to mark. `details` keys are `ProposedRoleInput`
 * keys. */
export class ProposalValidationError extends Error {
  details: Partial<Record<keyof ProposedRoleInput, string>>;

  constructor(message: string, details: Partial<Record<keyof ProposedRoleInput, string>>) {
    super(message);
    this.details = details;
  }
}

export async function fetchProposalFieldOptions(): Promise<ProposalFieldOptions> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/internal-playground/proposals/options`, {
    suppressErrorStatuses: [501],
  });
  if (res.status === 501) {
    const body = await res.json().catch(() => ({}));
    throw new ProposalOptionsUnavailableError(
      body.error || 'Airtable schema access is not available in this environment.'
    );
  }
  if (!res.ok) throw new Error(`Failed to load the proposal form options (${res.status})`);
  const body = await res.json();
  return body.options;
}

/**
 * What the roles being proposed would cost, before anything is filed.
 *
 * One call for many rows on purpose: the bulk form's rows share a Role and a department and differ
 * only by track and start date, so pricing them one at a time would be twenty-five round trips for
 * a lookup the proxy answers from one cached read. The single form uses the same call with one row.
 *
 * Never rejects on a role that cannot be priced — an unmatched role comes back as a row with
 * `unavailable` set, because a proposal with no salary band is still perfectly fileable.
 */
export async function fetchRoleCompensation(input: {
  role: string;
  peopleTeam: string;
  rows: { trackAndLevel?: string; plannedStartDate?: string }[];
}): Promise<RoleCompensation[]> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/internal-playground/compensation`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`Failed to price the role (${res.status})`);
  const body = await res.json();
  return Array.isArray(body.rows) ? body.rows : [];
}

/** The fields every role in a bulk submission shares — a `ProposedRoleInput` minus the two the
 * bulk form asks for per role. */
export type SharedProposalInput = Omit<ProposedRoleInput, 'plannedStartDate' | 'trackAndLevel'>;

/** One row of the bulk form. `trackAndLevel` stays optional, exactly as it is on the single form —
 * Airtable accepts a blank one and requiring it here only in bulk would be two rules for one
 * field. */
export interface BulkProposalRow {
  trackAndLevel?: string;
  plannedStartDate: string;
}

export interface BulkProposalInput {
  shared: SharedProposalInput;
  roles: BulkProposalRow[];
}

/** A 400 from the bulk route. `shared` marks a field every row carries; `rows` maps a ROW INDEX to
 * its own field errors, because "the date is missing" is useless without knowing which of twenty
 * rows it belongs to. Submission-level refusals (no rows, too many) carry both empty and say it in
 * `message`. */
export class BulkProposalValidationError extends Error {
  details: {
    shared: Partial<Record<keyof SharedProposalInput, string>>;
    rows: Record<number, Partial<Record<keyof BulkProposalRow, string>>>;
  };

  constructor(message: string, details: BulkProposalValidationError['details']) {
    super(message);
    this.details = details;
  }
}

/** Airtable accepted some rows and refused the rest — a real outcome, because Airtable takes ten
 * records per request and offers no transaction. Carries what DID land so the message can say so:
 * retrying a partial bulk write duplicates everything already created.
 *
 * `confirmed: false` means the last batch was never answered (a dropped connection), so
 * `createdIds` is a FLOOR and not a count — the card has to say "at least" rather than name a
 * number it cannot stand behind. */
export class BulkPartialFailureError extends Error {
  createdIds: string[];
  confirmed: boolean;

  constructor(message: string, createdIds: string[], confirmed = true) {
    super(message);
    this.createdIds = createdIds;
    this.confirmed = confirmed;
  }
}

/**
 * Whether the approvers were told, and if not, whether that is a problem.
 *
 * ⚠️ The two failing cases are NOT the same thing and must not be shown the same way.
 * `no_channel` / `no_token` mean the announcement is deliberately not configured in this
 * environment — the expected state while the feature is being rolled out — and a warning on every
 * submission during a planned off-period only teaches people to ignore warnings. Anything else
 * means Slack was configured and the post failed, and THEN somebody has to be told: a proposal the
 * approvers never heard about sits in the queue forever.
 */
export type NotifyOutcome = 'sent' | 'off' | 'failed';

export function notifyOutcome(body: { notified?: boolean; notifyReason?: string }): NotifyOutcome {
  if (body?.notified) return 'sent';
  return body?.notifyReason === 'no_channel' || body?.notifyReason === 'no_token' ? 'off' : 'failed';
}

export async function createProposedRole(
  input: ProposedRoleInput
): Promise<{ id: string; notify: NotifyOutcome; priorityDropped: boolean }> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/internal-playground/proposals`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
    suppressErrorStatuses: [400],
  });
  if (res.status === 400) {
    const body = await res.json().catch(() => ({}));
    throw new ProposalValidationError(body.error || 'The proposal is incomplete.', body.details || {});
  }
  if (!res.ok) throw new Error(`Failed to save the proposal (${res.status})`);
  const body = await res.json();
  // `priorityDropped`: filed, but the suggested priority could not be saved.
  return { id: body.id, notify: notifyOutcome(body), priorityDropped: Boolean(body.priorityDropped) };
}

/** File several proposed roles at once. The shared fields travel once, so the rows of one
 * submission cannot disagree about the title, manager or rationale. */
export async function createProposedRoles(
  input: BulkProposalInput
): Promise<{ ids: string[]; notify: NotifyOutcome; priorityDropped: boolean }> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/internal-playground/proposals/bulk`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
    suppressErrorStatuses: [400],
  });
  if (res.status === 400) {
    const body = await res.json().catch(() => ({}));
    throw new BulkProposalValidationError(body.error || 'The bulk proposal is incomplete.', {
      shared: body.details?.shared || {},
      rows: body.details?.rows || {},
    });
  }
  // 207 is a 2xx, so `authenticatedFetch` hands it back as a success — which it partly is. It is
  // checked BEFORE `res.ok` for exactly that reason.
  if (res.status === 207) {
    const body = await res.json().catch(() => ({}));
    throw new BulkPartialFailureError(
      body.error || 'Airtable accepted some of the roles and refused the rest.',
      body.ids || [],
      body.confirmed !== false
    );
  }
  if (!res.ok) throw new Error(`Failed to save the proposals (${res.status})`);
  const body = await res.json();
  return { ids: body.ids || [], notify: notifyOutcome(body), priorityDropped: Boolean(body.priorityDropped) };
}

/** Record "We will not backfill this position" for a leaver. */
export async function recordNoBackfill(employeeId: string): Promise<void> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/internal-playground/backfill-decisions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ employeeId }),
  });
  if (!res.ok) throw new Error(`Could not record the decision (${res.status})`);
}

/** Withdraw a recorded "not backfilled" decision. */
export async function withdrawNoBackfill(employeeId: string): Promise<void> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/internal-playground/backfill-decisions/${encodeURIComponent(employeeId)}`,
    { method: 'DELETE' }
  );
  if (!res.ok) throw new Error(`Could not withdraw the decision (${res.status})`);
}
